import { useState, type FormEvent } from "react";
import { useListUpdates, useCreateUpdate, useApproveUpdate, getListUpdatesQueryKey, useListCompanies } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "wouter";
import { Plus, CheckCircle2, Clock, FileEdit, Send } from "lucide-react";
import InfoTip from "@/components/info-tip";
import { useToast } from "@/hooks/use-toast";

const statusStyles: Record<string, { class: string; icon: typeof Clock }> = {
  draft: { class: "bg-gray-100 text-gray-800", icon: FileEdit },
  pending_review: { class: "bg-amber-100 text-amber-800", icon: Clock },
  approved: { class: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  published: { class: "bg-blue-100 text-blue-800", icon: Send },
};

export default function Updates({ cohortSlug }: { cohortSlug?: string } = {}) {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: updates, isLoading } = useListUpdates({
    status: statusFilter !== "all" ? statusFilter as "draft" | "pending_review" | "approved" | "published" : undefined,
  });
  const { data: companies } = useListCompanies();
  const createMutation = useCreateUpdate();
  const approveMutation = useApproveUpdate();

  const companyMap = new Map((companies ?? []).map(c => [c.id, c.name]));

  const handleCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    createMutation.mutate(
      {
        data: {
          companyId: parseInt(form.get("companyId") as string, 10),
          weekOf: new Date(form.get("weekOf") as string).toISOString(),
          highlights: (form.get("highlights") as string) || null,
          challenges: (form.get("challenges") as string) || null,
          asks: (form.get("asks") as string) || null,
          metrics: (form.get("metrics") as string) || null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListUpdatesQueryKey() });
          setDialogOpen(false);
          toast({ title: "Update created" });
        },
      }
    );
  };

  const handleApprove = (id: number) => {
    approveMutation.mutate(
      { id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListUpdatesQueryKey() });
          toast({ title: "Update approved" });
        },
      }
    );
  };

  return (
    <div className="space-y-6" data-testid="page-updates">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Weekly Updates</h1>
            <InfoTip content="The raw weekly updates submitted by founders. This is where check-in data lands before it goes through the review cycle. Use the Review Queue to move updates through the approval pipeline toward investor publishing." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Review and manage company updates across portfolio</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-update"><Plus className="w-4 h-4 mr-2" />New Update</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif">Create Weekly Update</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company</Label>
                  <select name="companyId" className="w-full h-9 rounded-md border bg-background px-3 text-sm" required data-testid="select-update-company">
                    <option value="">Select company</option>
                    {companies?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Week Of</Label>
                  <Input name="weekOf" type="date" required data-testid="input-update-week" />
                </div>
              </div>
              <div className="space-y-2"><Label>Highlights</Label><Textarea name="highlights" data-testid="input-update-highlights" /></div>
              <div className="space-y-2"><Label>Challenges</Label><Textarea name="challenges" data-testid="input-update-challenges" /></div>
              <div className="space-y-2"><Label>Asks</Label><Textarea name="asks" data-testid="input-update-asks" /></div>
              <div className="space-y-2"><Label>Metrics</Label><Input name="metrics" placeholder="e.g. MRR: $45K | Customers: 28" data-testid="input-update-metrics" /></div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending} data-testid="button-submit-update">
                {createMutation.isPending ? "Creating..." : "Create Update"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="select-filter-update-status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending_review">Pending Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-48" />)}</div>
      ) : updates && updates.length > 0 ? (
        <div className="space-y-4">
          {updates.map((update) => {
            const statusInfo = statusStyles[update.status] || statusStyles.draft;
            const StatusIcon = statusInfo.icon;
            return (
              <Card key={update.id} data-testid={`card-update-${update.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Link href={cohortSlug ? `/c/${cohortSlug}/companies/${update.companyId}` : `/companies/${update.companyId}`}>
                        <span className="font-medium hover:underline cursor-pointer" data-testid={`link-update-company-${update.id}`}>
                          {companyMap.get(update.companyId) ?? `Company #${update.companyId}`}
                        </span>
                      </Link>
                      <span className="text-sm text-muted-foreground">Week of {new Date(update.weekOf).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs gap-1 ${statusInfo.class}`}>
                        <StatusIcon className="w-3 h-3" />
                        {update.status.replace("_", " ")}
                      </Badge>
                      {(update.status === "pending_review" || update.status === "draft") && (
                        <Button variant="outline" size="sm" onClick={() => handleApprove(update.id)} disabled={approveMutation.isPending} data-testid={`button-approve-${update.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" />Approve
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {update.highlights && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Highlights</p>
                        <p className="text-sm">{update.highlights}</p>
                      </div>
                    )}
                    {update.challenges && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Challenges</p>
                        <p className="text-sm">{update.challenges}</p>
                      </div>
                    )}
                    {update.asks && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Asks</p>
                        <p className="text-sm">{update.asks}</p>
                      </div>
                    )}
                    {update.metrics && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Metrics</p>
                        <p className="text-sm font-mono">{update.metrics}</p>
                      </div>
                    )}
                  </div>

                  {update.approvedBy && (
                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                      Approved by {update.approvedBy} on {update.approvedAt ? new Date(update.approvedAt).toLocaleDateString() : ""}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FileEdit className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No updates found</p>
            <p className="text-sm text-muted-foreground mt-1">{statusFilter !== "all" ? "Try changing the filter" : "Create your first weekly update"}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
